<!-- MitManagePeopleList.vue -->
<template>
  <v-container fluid>
    <!-- Search and Add New Person Row -->
    <v-row>
      <v-col>
        <MitPeopleSearch
          @search-input="updateSearchTerm"
          @clear-filters="clearFilters"
        />
      </v-col>
      <v-col>
        <v-btn color="primary" @click="openAddPersonDialog">Add New Person</v-btn>
      </v-col>
    </v-row>

    <!-- Filters Row -->
    <v-row class="filters">
      <v-col>
        <v-select
          v-model="selectedFilterName"
          :items="filterOptions"
          label="Select Filter"
          outlined
        ></v-select>
      </v-col>
      <v-col>
        <v-text-field
          v-model="newFilterValue"
          label="Search on Selected Filter"
          outlined
        />
      </v-col>
    </v-row>

    <BaseMitLoading :show="loading" :fullscreen="false"/>

    <BaseMitDataTable
      dense
      title="People List"
      :page="paging.page"
      :pageSize="paging.pageSize"
      :totalItems="paging.totalItems"
      :pageSizes="{ itemsPerPageOptions: [10, 20, 50, 100] }"
      :headers="headers"
      :items="filteredPeople"
      :editable="false"
      :singleExpand="true"
      :showExpanded="true"
      :expanded.sync="expanded"
      @pagination="updatePagination"
    >
    <template v-slot:item.fname="{ item }">
    <span :style="item.status === 'Inactive' ? 'color: red;' : ''">
      {{ item.fname }}
    </span>
  </template>

  <template v-slot:item.lname="{ item }">
    <span :style="item.status === 'Inactive' ? 'color: red;' : ''">
      {{ item.lname }}
    </span>
  </template>
      <!-- Location Slot -->
      <template v-slot:item.location="{ item }">
        <div>
          <template v-if="item.location.spaceId !== ''">
            {{ item.location.spaceId }}
          </template>
          <template v-else-if="item.location.setupComments">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <i 
                  class="v-icon mdi mdi-text-box-outline theme--light" 
                  v-on="on"
                ></i>
              </template>
              <span>
                Needs a space
                <template v-if="item.location.setupComments"><br />
                  Comments: {{ item.location.setupComments }}
                </template>
              </span>
            </v-tooltip>
          </template>
        </div>
      </template>

      <!-- Edit slot -->
      <template v-slot:item.edit="{ item }">
        <v-btn icon :to="`/user/${item.csailId}`">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </template>

      <!-- Expanded Row slot  -->
      <template v-slot:expanded-item="{ item }">
        <td class="pa-1" :colspan="headers.length">
          <v-card>
            <v-card-text>
              <h4>{{ item.fname }} {{ item.lname }}</h4>
              <strong>MIT ID:</strong> {{ item.mitId }}</br>
              <strong>CSAIL ID:</strong> {{ item.csailId }}</br>
              <strong>Supervisor:</strong> {{ item.supervisor }}</br>
              <strong>Status:</strong> 
                <span                 
                  :style="item.status === 'Active' ? 'color: green;' : item.status === 'Inactive' ? 'color: red;' : ''" >{{ item.status }}</span></br>
              <strong>Location:</strong> {{ item.location.spaceId || 'N/A' }}</br>
              <strong>Setup Comments:</strong> {{ item.location.setupComments || 'No comments available' }}
            </v-card-text>
          </v-card>
        </td>
      </template>

      <template v-slot:no-data>
        <v-alert type="warning">No people found.</v-alert>
      </template>
    </BaseMitDataTable>

    <!-- Add New Person Dialog  -->
    <AddNewPersonDialog
      :personDialogOpenClose="addPersonDialog"
      :roles="roles"
      :typeOptions="typeOptions"
      :statusOptions="statusOptions"
      @update:personDialogOpenClose="addPersonDialog = $event"
      @add-person="addPerson"
    />
  </v-container>
</template>

<script>
  import MitPeopleService from '../../services/MitPeopleService';
  import BaseMitDataTable from '../../../base/components/BaseMitDataTable.vue';
  import BaseMitLoading from '../../../base/components/BaseMitLoading.vue';
  import MitPeopleSearch from './MitPeopleSearch.vue';
  import AddNewPersonDialog from './AddNewPersonDialog.vue'; 

  export default {
    name: 'MitManagePeopleList',
    components: {
      BaseMitDataTable,
      BaseMitLoading,
      MitPeopleSearch,
      AddNewPersonDialog, 
    },
    data() {
      return {
        people: [],
        loading: true,
        addPersonDialog: false,
        valid: false,
        selectedComments: null,
        selectedFilterName: null,
        newFilterValue: '',
        roles: [],
        filterOptions: [
          { text: 'Last Name', value: 'lname' },
          { text: 'CSAIL ID', value: 'csailId' },
          { text: 'MIT ID', value: 'mitId' },
          { text: 'First Name', value: 'fname' },
          { text: 'Supervisor', value: 'supervisor' },
          { text: 'Status', value: 'status' },
          { text: 'Members with Rooms Assigned', value: 'roomsAssigned' },
          { text: 'Members without Rooms Assigned', value: 'noRoomsAssigned' },
        ],
        headers: [
          { text: 'First Name', value: 'fname' },
          { text: 'Last Name', value: 'lname' },
          { text: 'MIT ID', value: 'mitId' },
          { text: 'CSAIL ID', value: 'csailId' },
          { text: 'Location', value: 'location' },
          { text: 'Edit', value: 'edit', sortable: false },
        ],
        typeOptions: ['Employer', 'Grad Student', 'Undergrad Student', 'Urop'],
        statusOptions: ['Incoming', 'Pending activation', 'Active'],
        paging: {
          page: 1,
          pageSize: 25,
          totalItems: 0,
        },
        expanded: [],
        searchTerm: '',
      };
    },
    computed: {
      filteredPeople() {
        let filtered = this.people;

        // Apply search term
        if (this.searchTerm) {
          const searchTerm = this.searchTerm.toLowerCase();
          filtered = filtered.filter((person) => {
            return (
              person.fname.toLowerCase().includes(searchTerm) ||
              person.lname.toLowerCase().includes(searchTerm) ||
              person.mitId.toLowerCase().includes(searchTerm) ||
              person.csailId.toLowerCase().includes(searchTerm) ||
              (person.location.spaceId &&
                person.location.spaceId.toLowerCase().includes(searchTerm)) ||
              (person.location.setupComments &&
                person.location.setupComments.toLowerCase().includes(searchTerm))
            );
          });
        }

        // Apply filters
        if (this.selectedFilterName === 'roomsAssigned') {
          filtered = filtered.filter(
            (person) =>
              person.location &&
              person.location.spaceId &&
              person.location.spaceId.trim() !== ''
          );

          if (this.newFilterValue) {
            const filterTerm = this.newFilterValue.toLowerCase();
            filtered = filtered.filter((person) =>
              person.location.spaceId.toLowerCase().includes(filterTerm)
            );
          }
        } else if (this.selectedFilterName === 'noRoomsAssigned') {
          filtered = filtered.filter(
            (person) =>
              (!person.location.spaceId || person.location.spaceId.trim() === '') &&
              (!this.newFilterValue ||
                (person.location.setupComments &&
                  person.location.setupComments
                    .toLowerCase()
                    .includes(this.newFilterValue.toLowerCase())))
          );
        } else if (this.selectedFilterName) {
          filtered = filtered.filter((person) => {
            return (
              person[this.selectedFilterName] &&
              person[this.selectedFilterName]
                .toString()
                .toLowerCase()
                .includes(this.newFilterValue.toLowerCase())
            );
          });
        }

        return filtered;
      },
    },
    created() {
      this.loadPeople();
      this.loadRoles();
    },
    methods: {
      async loadPeople() {
        try {
          this.loading = true;
          const response = await MitPeopleService.getPeople();
          this.people = response;
          this.paging.totalItems = this.people.length;
          console.log('Loaded people:', this.people);
        } catch (error) {
          console.error('Error loading people:', error);
        } finally {
          this.loading = false;
        }
      },
      async loadRoles() {
        try {
          const response = await MitPeopleService.getRoles();
          this.roles = response.roles;
        } catch (error) {
          console.error('Error loading roles:', error);
        }
      },
      openAddPersonDialog() {
        this.addPersonDialog = true;
      },
      clearFilters() {
        this.selectedFilterName = null;
        this.newFilterValue = '';
      },
      addPerson(newPerson) {
        this.people.push(newPerson);
        this.paging.totalItems += 1;
      },
      updatePagination(pagination) {
        if (pagination.itemsLength > 0) {
          this.paging.page = pagination.page;
          this.paging.pageSize = pagination.itemsPerPage;
        }
      },
      updateSearchTerm(term) {
        this.searchTerm = term;
      },
    },
  };
</script>

<style scoped>
  .elevation-1 {
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }

  .filters {
    padding: 15px;
    border-bottom: 1px solid #ccc;
    margin-bottom: 20px;
  }

  .light-border .v-input__control {
    border-color: rgba(0, 0, 0, 0.268);
    border: none !important;
    box-shadow: none;
  }

  .light-border .v-input__control:hover {
    border-color: rgba(0, 0, 0, 0.268);
    border: none !important;
  }

  .required-field .v-input__label::after {
    content: ' *';
    color: red;
    font-weight: bold;
  }

  /* Role checkboxes */
  .role-checkbox {
    margin-bottom: -35px;
  }

  .v-text-field__details {
    display: none;
  }
</style>
